import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import AddTransactionModal from '../components/AddTransactionModal';
import TransactionList from '../components/TransactionList';
import DateRangeDisplay from '../components/DateRangeDisplay';
import { ledgerAPI, transactionAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPlus, FiCalendar, FiTag, FiFilter } from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const LedgerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, toneEnabled } = useTone();

  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTxnModal, setShowTxnModal] = useState(false);

  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const dateRange = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'current_month':
        return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'last_3_months':
        return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
      case 'all':
      default:
        return { start: null, end: null };
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchLedgerData();
  }, [id, dateFilter]);

  const fetchLedgerData = async () => {
    setLoading(true);
    try {
      const params = { ledger_id: id, limit: 100 };
      if (dateRange.start) params.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) params.end_date = format(dateRange.end, 'yyyy-MM-dd');

      const [ledgerRes, txnRes] = await Promise.all([
        ledgerAPI.getOne(id),
        transactionAPI.getAll(params),
      ]);

      setLedger(ledgerRes.data.data.ledger);
      setTransactions(txnRes.data.data.transactions || []);
    } catch (error) {
      toast.error('Failed to load ledger details');
      navigate('/ledgers');
    } finally {
      setLoading(false);
    }
  };

  const handleTxnSuccess = () => {
    fetchLedgerData();
    setShowTxnModal(false);
  };

  /**
   * ENTERPRISE RULE (Section 15-17): Internal transfers display as bank-like entries
   * - "Transfer to X" → outgoing (expense-like, negative, red)
   * - "Transfer from X" → incoming (income-like, positive, green)
   * This makes internal transfers behave like bank statement entries
   */
  const getTransactionTypeInfo = (type, txn = null) => {
    // Handle internal_transfer with direction awareness for bank-statement style display
    if (type === 'internal_transfer' && txn) {
      const desc = (txn.description || '').toLowerCase();
      // "Transfer to X" means money went OUT of this ledger → expense-like
      if (desc.startsWith('transfer to')) {
        return {
          label: toneEnabled ? 'Transfer bheja' : 'Transfer Out',
          icon: '−',
          color: '#E74C3C',
          class: 'expense'
        };
      }
      // "Transfer from X" means money came IN to this ledger → income-like
      if (desc.startsWith('transfer from')) {
        return {
          label: toneEnabled ? 'Transfer aaya' : 'Transfer In',
          icon: '+',
          color: '#27AE60',
          class: 'income'
        };
      }
    }

    const types = {
      income: { label: toneEnabled ? 'Payment aaya' : 'Income', icon: '+', color: '#27AE60', class: 'income' },
      income_received: { label: toneEnabled ? 'Payment aaya' : 'Income', icon: '+', color: '#27AE60', class: 'income' },
      expense: { label: toneEnabled ? 'Kharcha kiya' : 'Expense', icon: '−', color: '#E74C3C', class: 'expense' },
      expense_paid: { label: toneEnabled ? 'Kharcha kiya' : 'Expense', icon: '−', color: '#E74C3C', class: 'expense' },
      internal_transfer: { label: toneEnabled ? 'Transfer' : 'Transfer', icon: '↔', color: '#7F8C8D', class: 'transfer' },
      payable: { label: toneEnabled ? 'Dena hai' : 'Payable', icon: '◯', color: '#F39C12', class: 'neutral' },
      receivable: { label: toneEnabled ? 'Lena hai' : 'Receivable', icon: '◎', color: '#3498DB', class: 'neutral' },
      scheduled: { label: toneEnabled ? 'Yaad' : 'Scheduled', icon: '⏰', color: '#9B59B6', class: 'neutral' },
    };
    return types[type] || { label: type, icon: '•', color: '#7F8C8D', class: 'neutral' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  if (!ledger) {
    return (
      <Layout>
        <div className="page-container">
          <p>{toneEnabled ? 'Khata nahi mila' : 'Ledger not found'}</p>
        </div>
      </Layout>
    );
  }

  const totalIncome = parseFloat(ledger.total_income || 0);
  const totalExpense = parseFloat(ledger.total_expense || 0);
  const balance = parseFloat(ledger.balance || 0);

  return (
    <Layout>
      <div className="page-container">
        {/* Header */}
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/ledgers')} title={toneEnabled ? 'Wapas' : 'Back'}>
              <FiArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: ledger.color || '#4A90E2',
                    display: 'inline-block',
                  }}
                />
                {ledger.name}
              </h1>
              {ledger.description && (
                <p style={{ margin: '4px 0 0', color: '#7F8C8D', fontSize: '0.9rem' }}>
                  {ledger.description}
                </p>
              )}
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowTxnModal(true)}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {toneEnabled ? 'Lenden Jodo' : 'Add Transaction'}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards" style={{ marginBottom: 24 }}>
          <div className="summary-card income">
            <h3>{toneEnabled ? 'Saara aaya' : 'Total Income'}</h3>
            <p className="amount">{formatAmount(totalIncome)}</p>
          </div>
          <div className="summary-card expense">
            <h3>{toneEnabled ? 'Saara gaya' : 'Total Expense'}</h3>
            <p className="amount">{formatAmount(totalExpense)}</p>
          </div>
          <div className="summary-card balance">
            <h3>{toneEnabled ? 'Bacha hua' : 'Current Balance'}</h3>
            <p className="amount">{formatAmount(balance)}</p>
          </div>
        </div>

        {/* Ledger Meta */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7F8C8D' }}>
              <FiTag size={16} />
              <span>{toneEnabled ? 'Tarika' : 'Type'}: <strong style={{ color: '#2c3e50', textTransform: 'capitalize' }}>{ledger.ledger_type || 'general'}</strong></span>
            </div>
            {ledger.is_archived && (
              <span className="badge" style={{ backgroundColor: '#95A5A6' }}>
                {toneEnabled ? 'Band hai' : 'Archived'}
              </span>
            )}
          </div>
        </div>

        {/* Date Filter & Range Display */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiFilter size={16} style={{ color: '#7F8C8D' }} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.9rem' }}
            >
              <option value="all">{toneEnabled ? 'Sab Dikhaao' : 'All Time'}</option>
              <option value="current_month">{toneEnabled ? 'Is Mahine' : 'Current Month'}</option>
              <option value="last_month">{toneEnabled ? 'Pichle Mahine' : 'Last Month'}</option>
              <option value="last_3_months">{toneEnabled ? 'Pichle 3 Mahine' : 'Last 3 Months'}</option>
            </select>
          </div>
          {dateRange.start && dateRange.end && (
            <DateRangeDisplay startDate={dateRange.start} endDate={dateRange.end} toneEnabled={toneEnabled} />
          )}
        </div>

        {/* Transactions List */}
        <div className="card">
          <h2 style={{ marginTop: 0, marginBottom: 16 }}>
            {toneEnabled ? 'Saare Lenden' : 'All Transactions'}
            <span style={{ color: '#7F8C8D', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: 8 }}>
              ({transactions.length})
            </span>
          </h2>

          {transactions.length === 0 ? (
            <p style={{ color: '#7F8C8D', textAlign: 'center', padding: 20 }}>
              {toneEnabled ? 'Koi lenden nahi mila' : 'No transactions found'}
            </p>
          ) : (
            <TransactionList
              transactions={transactions}
              columns={['date', 'type', 'description', 'category', 'amount']}
              getTypeInfo={getTransactionTypeInfo}
              formatDate={formatDate}
              t={t}
              toneEnabled={toneEnabled}
              showActions={false}
            />
          )}
        </div>

        {/* Transaction Modal */}
        {showTxnModal && (
          <AddTransactionModal
            isOpen={showTxnModal}
            onClose={() => setShowTxnModal(false)}
            onSuccess={handleTxnSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default LedgerDetail;
